import { createContext, ReactNode, useContext } from 'react';
import { useToast } from "hooks/useToast";
import { api } from "services/api";
import { useSession } from "hooks/useSession";
import axios, { AxiosResponse, CancelToken, CancelTokenSource } from 'axios';

type RequestType = "post" | "get" | "put" | "delete" | 'patch';
type ErrorType = "connection" | "cancellation"

interface AuthProviderProps {
  children: ReactNode
}

interface AuthConnectionProps {
  url: string;
  body?: {};
  header?: {};
  type: RequestType;
  cancelToken?: CancelToken;
  errorMessage?: string;
  successMessage?: string;
  authenticate?: boolean;
  endSession?: boolean;
  headerProps?: object;
}

export interface AuthConnectionErrorProps {
  errorType: ErrorType,
  errorMessage?: string,
  errorCode?: number,
}

interface Header {
  [key: string]: any,
}

interface AuthContextProps {
  connectApi: (data:AuthConnectionProps ) => Promise<any>;
  connectAllApi: (data:AuthConnectionProps[] ) => Promise<any>;
  getSource: () => CancelTokenSource,
  setSource: () => void,
  cancelRequest: () => void,
}

const AuthContext = createContext({} as AuthContextProps);
const cancelMessage = 'Operation canceled due to new request'

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { setToast } = useToast();
  const { signOut } = useSession();
  let source = axios.CancelToken.source();

  const getSource = () => {
    return source;
  }

  const setSource = () => {
    source = axios.CancelToken.source();
  }

  const cancelRequest = () => {
    source && source.cancel(cancelMessage);
    setSource();
  }

  const connectApi = async ({
    url,
    body = {},
    type,
    errorMessage,
    successMessage,
    authenticate = true,
    endSession = true,
    headerProps = {}
  }: AuthConnectionProps): Promise<any> => {
    var header:Header = {
      ...headerProps,
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS"
      },
      cancelToken: source.token,
    };
    if (authenticate) {
      const token = localStorage.getItem("backoffice@confi");
      header.headers["Authorization"] = `Bearer ${token}`;
    }
    let response;

    try {
      if (type === 'get' || type === 'delete')
        response = await api[type](url, header);
      else
        response = await api[type](url, body, header);
  
      successMessage &&
      setToast({
        message: successMessage,
        type: 'success',
        isActive: true,
        timeToRemoveToast: 3000
      });
      return response;
    } catch (error: any) {
      const errorResponse:AuthConnectionErrorProps = {
        errorType: error.message === cancelMessage ? 'cancellation' : 'connection',
        errorCode: error.response?.status,
        errorMessage: error.response?.data?.Message ?? error.message,
      };

      if (errorResponse.errorType === 'connection') {
        if (endSession && errorResponse.errorCode === 401) {
          sessionStorage.setItem('unauthorized', 'true');
          window.location.replace("/");
          signOut();
        } else {
          setToast({
            message: `Erro ${errorResponse.errorCode ?? ''}: ${errorMessage ?? errorResponse.errorMessage}`,
            isActive: true,
            type: 'error',
            timeToRemoveToast: 3000
          })
        }
      }
      throw errorResponse;
    }
  }

  const connectAllApi = async (data: AuthConnectionProps[]): Promise<PromiseSettledResult<AxiosResponse<any>>[]> => {  
    var defaultHeader:Header = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS"
      },
      cancelToken: source.token,
    };
    try {
      const responses = await Promise.allSettled(
        data.map(item => {
          // default values
          item.body = typeof item.body === 'undefined' ?  {} : item.body;
          item.authenticate = typeof item.authenticate === 'undefined' ?  true : item.authenticate;
          item.endSession = typeof item.endSession === 'undefined' ?  true : item.endSession;
          item.headerProps = typeof item.headerProps === 'undefined' ?  {} : item.headerProps;

          var header = {
            ...item.headerProps,
            ...defaultHeader
          }
          if (item.authenticate) {
            const token = localStorage.getItem("backoffice@confi");
            header.headers["Authorization"] = `Bearer ${token}`;
          }
          if (item.type === 'get' || item.type === 'delete')
            return api[item.type](item.url, header);
          else
            return api[item.type](item.url, item.body, header);
        })
      );

      responses.map((response, index) => {     
        if(response.status === "rejected") {
          if(data[index].endSession && response.reason?.response?.status === 401){
            sessionStorage.setItem('unauthorized', 'true');
            window.location.replace("/");
            signOut();
          } else {
            setToast({
              message: `Erro ${response.reason?.response?.status}: ${data[index].errorMessage ?? "Falha ao se comunicar com o servidor"}`,
              type: 'error',
              isActive: true,
              timeToRemoveToast: 3000
            })
          }
        }
      });
      return responses;
    } catch {
      throw 'catch'
    }
  }

  return (
    <AuthContext.Provider value={{ 
      connectApi,
      connectAllApi,
      getSource,
      setSource,
      cancelRequest,
    }}>
      {children}
    </AuthContext.Provider>
  );

}

export const useAuth = () => useContext(AuthContext);